import { concat, get, includes, intersection, values } from 'lodash';
import { createSelector } from 'reselect';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';
import { selectCurrentMaster, selectCurrentUser } from '@lib/selectors';
import configService from '@lib/services/configService';

export type Permissions = {
  allProducts: (ProvisioningEnum | RolloutEnum)[];
  hasAccessAdminPage: boolean;
  hasAccessToLabs: boolean;
  hasAccessBroadcastApprove: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isLumaUser: boolean;
  isDemoAccount: boolean;
  isMaster: boolean;
  isSubaccount: boolean;
  isDemoEnv: boolean;
  provisioning: ProvisioningEnum[];
  respectProvisioning: boolean;
  rollouts: RolloutEnum[];
  hasAccessWaitlistsTags: boolean;
  hasAccessInsuranceVerification: boolean;
  isNextyNext?: boolean;
  isNextyPreview?: boolean;
};

const selectPermissions = createSelector(selectCurrentUser, selectCurrentMaster, (currentUser, currentMaster) => {
  if (!currentUser) return;

  const isAdmin = includes(currentUser.roles, 'admin');
  const isManager = includes(currentUser.roles, 'manager');
  const isSubaccount = includes(currentUser.roles, 'subaccount');
  // @ts-ignore
  const isMaster = currentUser._id === currentUser.user;
  const isLumaUser = includes(currentUser.email, '@lumahealth.io');
  const isDemoAccount = /demo\+[\w]+@lumahealth\.io/.test(currentUser.email);
  const isDemoEnv = configService.getPodName() === 'demo';

  const respectProvisioning = get(currentMaster, 'salesforceData.respectProvisioning', false);
  let provisioning;

  if (!respectProvisioning) {
    // if respectProvisioning is `false` give access to all provisioning.
    provisioning = values(ProvisioningEnum);
  } else {
    // if respectProvisoning is `true`:
    provisioning = get(currentUser, 'salesforceData.provisioning', []);
    // and its not the master accounts you get the intersetion between the current user provisoning and the master
    if (!isMaster) {
      const provisioningMaster = get(currentMaster, 'salesforceData.provisioning', []);
      provisioning = intersection(provisioning, provisioningMaster);
    }
  }
  // if its a provider we ignore the respectProvisioning and always use the current providers provisioning
  if (currentUser.type === 'doctor') {
    const provisioningMaster = get(currentMaster, 'salesforceData.provisioning', []);
    // Only add chat if respect provisioning is off OR the master has chat provisioning.
    const chatProvisioning = respectProvisioning
      ? provisioningMaster.filter((permission: ProvisioningEnum) => permission === ProvisioningEnum.Chat)
      : [ProvisioningEnum.Chat];

    provisioning = [...get(currentUser, 'salesforceData.provisioning', []), ...chatProvisioning];
  }

  // NOTE: uncomment to enable all provisioning:
  // provisioning = values(ProvisioningEnum);

  // NOTE: uncomment to enable all rollouts:
  // const rollouts = [...values(RolloutEnum), 'nexty-next'];
  const rollouts = get(currentMaster, 'rollouts', []);

  return {
    allProducts: concat(provisioning, rollouts),
    hasAccessAdminPage: !!currentUser?.impersonate?.length,
    hasAccessToLabs: get(currentUser, 'settings.labs.enabled'),
    hasAccessBroadcastApprove: isAdmin || isManager || isMaster,
    isAdmin,
    isDemoAccount,
    isManager,
    isLumaUser,
    isMaster,
    isSubaccount,
    isDemoEnv,
    provisioning,
    respectProvisioning,
    rollouts,
    hasAccessWaitlistsTags: isDemoAccount || includes(rollouts, RolloutEnum.WaitlistsTags),
    hasAccessInsuranceVerification: isDemoEnv || includes(provisioning, ProvisioningEnum.InsuranceVerification),
  } as Permissions;
});

export default selectPermissions;
